package com.havn.app.features.leads.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnBackground
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnDanger
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnGoldLight
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnInfiniteListLoadEffect
import com.havn.app.core.designsystem.HavnPullToRefreshBox
import com.havn.app.core.designsystem.HavnIvory
import com.havn.app.core.designsystem.HavnLeadCard
import com.havn.app.core.designsystem.HavnListDefaults
import com.havn.app.core.designsystem.HavnListFilterChip
import com.havn.app.core.designsystem.HavnListRowSeparator
import com.havn.app.core.designsystem.HavnMetric
import com.havn.app.core.designsystem.HavnMuted
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnRefreshOverlayIndicator
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnSkeletonBlock
import com.havn.app.core.designsystem.HavnSkeletonListFooter
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnStatus
import com.havn.app.core.designsystem.HavnStatusBadge
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextMuted
import com.havn.app.core.designsystem.HavnTextPrimary
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.HavnTopHeaderTextStyle
import com.havn.app.core.designsystem.HavnWebRefreshAction
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.designsystem.havnRefreshStartGate
import com.havn.app.core.designsystem.havnNeedsNativeTabBarContentInset
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnAsyncImage
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.havnContactAvatarPreviewSource
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.features.followups.presentation.FollowUpComposerContent
import com.havn.app.features.followups.presentation.FollowUpsViewModel
import com.havn.app.features.leads.domain.Lead
import com.havn.app.features.leads.domain.LeadFollowUp
import com.havn.app.features.leads.domain.LeadPropertySource
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.tasks.domain.HavnTask
import com.havn.app.features.tasks.presentation.LinkedTasksSection
import com.havn.app.ui.theme.LocalHavnThemeTokens

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LeadsScreen(
    viewModel: LeadsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    onSendFollowUp: (String) -> Unit = {},
    onFollowUpCompleted: () -> Unit = {},
    onCreateTask: (Lead) -> Unit = {},
    canUseAiFollowUps: Boolean = true,
    onAiUpgradeRequired: () -> Unit = {},
    detailBackLabel: String? = null,
    onBackFromDetail: (() -> Unit)? = null,
    linkedTasks: List<HavnTask> = emptyList(),
    onOpenTask: (String) -> Unit = {},
    scrollToTopSignal: Int = 0
) {
    val state by viewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current

    HavnPushNavigation(
        targetState = state.mode,
        depthFor = LeadsMode::navigationDepth
    ) { mode ->
        if (mode == LeadsMode.List) {
            LeadsListHome(
                state = state,
                viewModel = viewModel,
                scrollToTopSignal = scrollToTopSignal,
                onUpgradePlan = onAiUpgradeRequired
            )
            return@HavnPushNavigation
        }

        LeadDetailHome(
            state = state,
            viewModel = viewModel,
            followUpsViewModel = followUpsViewModel,
            onSendFollowUp = onSendFollowUp,
            onFollowUpCompleted = onFollowUpCompleted,
            onCreateTask = onCreateTask,
            canUseAiFollowUps = canUseAiFollowUps,
            onAiUpgradeRequired = onAiUpgradeRequired,
            detailBackLabel = detailBackLabel,
            onBackFromDetail = onBackFromDetail,
            linkedTasks = linkedTasks,
            onOpenTask = onOpenTask
        )
    }
}

@Composable
@OptIn(ExperimentalMaterial3Api::class)
private fun LeadsListHome(
    state: LeadsUiState,
    viewModel: LeadsViewModel,
    scrollToTopSignal: Int,
    onUpgradePlan: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val listState = rememberLazyListState()
    var handledScrollToTopSignal by remember { mutableStateOf(scrollToTopSignal) }
    LaunchedEffect(scrollToTopSignal) {
        if (scrollToTopSignal > handledScrollToTopSignal) {
            if (listState.firstVisibleItemIndex == 0 && listState.firstVisibleItemScrollOffset == 0) {
                viewModel.refresh()
            } else {
                listState.animateScrollToItem(0)
            }
        }
        handledScrollToTopSignal = scrollToTopSignal
    }
    var gestureStartedAtTop by remember { mutableStateOf(false) }
    val bottomContentPadding = if (havnNeedsNativeTabBarContentInset) 132.dp else 18.dp
    HavnInfiniteListLoadEffect(
        listState = listState,
        enabled = state.hasMoreLeads,
        isLoading = state.isLoading || state.isLoadingMore,
        itemCount = state.filteredLeads.size,
        onLoadMore = viewModel::loadMoreLeads
    )
    HavnPullToRefreshBox(
        isRefreshing = state.isLoading,
        onRefresh = {
            if (gestureStartedAtTop) {
                viewModel.refresh()
            }
        },
        modifier = Modifier
            .fillMaxSize()
            .havnRefreshStartGate(listState) { gestureStartedAtTop = it }
            .background(Brush.verticalGradient(listOf(theme.gradientStart, theme.background, theme.gradientEnd)))
    ) {
        LazyColumn(
            state = listState,
            modifier = Modifier
                .fillMaxSize(),
            contentPadding = PaddingValues(start = 18.dp, top = 18.dp, end = 18.dp, bottom = bottomContentPadding),
            verticalArrangement = Arrangement.spacedBy(HavnListDefaults.headerToSearchGap)
        ) {
            item(key = "leads-header") {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = strings.navLeads,
                        modifier = Modifier.weight(1f),
                        color = theme.primaryText,
                        style = HavnTopHeaderTextStyle
                    )
                }
            }

            item(key = "leads-filters") {
                Column(verticalArrangement = Arrangement.spacedBy(HavnListDefaults.searchToFilterGap)) {
                    HavnSearchField(
                        value = state.searchQuery,
                        onValueChange = viewModel::updateSearchQuery,
                        placeholder = strings.searchLeadsPlaceholder,
                        modifier = Modifier.fillMaxWidth()
                    )

                    LeadListSegments(
                        selectedTemperature = state.temperatureFilter,
                        inquirySelected = state.inquiryFilter,
                        onTemperatureSelected = viewModel::selectTemperatureFilter,
                        onInquirySelected = viewModel::selectInquiryFilter
                    )
                }
            }

            state.error?.let { message ->
                item(key = "leads-error") {
                    UpgradeableErrorText(message = message, onDismiss = viewModel::clearError, onUpgradePlan = onUpgradePlan)
                }
            }

            when {
                (!state.hasLoadedInitialPage || state.isLoading) && state.leads.isEmpty() -> {
                    items(8, key = { "lead-skeleton-$it" }) {
                        LeadCardSkeleton()
                    }
                }
                state.filteredLeads.isEmpty() -> {
                    item(key = "leads-empty") {
                        EmptyState(
                            title = strings.noLeadsFoundTitle,
                            body = if (state.leads.isEmpty()) strings.noLeadsEmptyBody else strings.noLeadsFilteredBody,
                            actionText = null,
                            onAction = null
                        )
                    }
                }
                else -> {
                    itemsIndexed(state.filteredLeads, key = { _, lead -> lead.id }) { index, lead ->
                        CompactLeadRow(
                            lead = lead,
                            onClick = { viewModel.openLead(lead.id) }
                        )
                        if (index < state.filteredLeads.lastIndex) {
                            HavnListRowSeparator()
                        }
                    }
                    if (state.hasMoreLeads) {
                        item(key = "leads-loading-more") {
                            HavnSkeletonListFooter()
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun LeadsListContent(
    state: LeadsUiState,
    viewModel: LeadsViewModel
) {
    val strings = LocalHavnStrings.current
    LeadFilters(
        searchQuery = state.searchQuery,
        selectedPropertyId = state.selectedPropertyId,
        selectedTemperature = state.temperatureFilter,
        properties = state.propertyFilters,
        onSearchChanged = viewModel::updateSearchQuery,
        onPropertySelected = viewModel::selectPropertyFilter,
        onTemperatureSelected = viewModel::selectTemperatureFilter
    )

    if (state.isLoading) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            repeat(6) {
                LeadCardSkeleton()
            }
        }
    } else if (state.filteredLeads.isEmpty()) {
        EmptyState(
            title = strings.noLeadsFoundTitle,
            body = if (state.leads.isEmpty()) {
                strings.noLeadsEmptyBody
            } else {
                strings.noLeadsFilteredBody
            },
            actionText = null,
            onAction = null
        )
    } else {
        state.filteredLeads.forEach { lead ->
            HavnLeadCard(
                name = lead.fullName,
                contact = lead.sourceProperty.title,
                sourceProperty = lead.sourceOpenHouse.displayTitle,
                avatarDataUrl = lead.avatarDataUrl,
                temperature = lead.temperature.toStatus(),
                lastActivityTime = "${strings.commonCheckedIn} ${lead.checkedInAt.compactDateTime(strings)}",
                onClick = { viewModel.openLead(lead.id) }
            )
        }
    }
}

@Composable
private fun CompactLeadSearchField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier.height(42.dp),
        shape = RoundedCornerShape(10.dp),
        color = theme.surface.copy(alpha = 0.84f),
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.82f))
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            HavnIcon(
                icon = HavnIconKey.Search,
                contentDescription = null,
                tint = theme.secondaryText,
                modifier = Modifier.size(17.dp)
            )
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                singleLine = true,
                textStyle = MaterialTheme.typography.bodyMedium.copy(color = theme.primaryText),
                cursorBrush = androidx.compose.ui.graphics.SolidColor(theme.primaryAccent),
                modifier = Modifier.weight(1f),
                decorationBox = { innerTextField ->
                    if (value.isBlank()) {
                        Text(
                            placeholder,
                            color = theme.secondaryText,
                            style = MaterialTheme.typography.bodyMedium,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    innerTextField()
                }
            )
        }
    }
}

@Composable
private fun LeadListSegments(
    selectedTemperature: LeadTemperature?,
    inquirySelected: Boolean,
    onTemperatureSelected: (LeadTemperature?) -> Unit,
    onInquirySelected: () -> Unit
) {
    val strings = LocalHavnStrings.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(HavnListDefaults.filterChipGap)
    ) {
        HavnListFilterChip(
            label = strings.commonAll,
            selected = selectedTemperature == null && !inquirySelected,
            onClick = { onTemperatureSelected(null) },
            modifier = Modifier.weight(0.78f)
        )
        LeadTemperature.entries.forEach { temperature ->
            HavnListFilterChip(
                label = temperature.localizedLabel(strings),
                selected = selectedTemperature == temperature && !inquirySelected,
                onClick = { onTemperatureSelected(temperature) },
                modifier = Modifier.weight(1f)
            )
        }
        HavnListFilterChip(
            label = strings.commonInquiry,
            selected = inquirySelected,
            onClick = onInquirySelected,
            modifier = Modifier.weight(1.32f)
        )
    }
}

@Composable
private fun CompactLeadRow(
    lead: Lead,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        onClick = onClick,
        color = Color.Transparent,
        modifier = Modifier
            .fillMaxWidth()
            .height(58.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 2.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            LeadAvatar(lead.fullName, lead.avatarThumbnailDataUrl)
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.Center
            ) {
                Text(
                    text = lead.fullName,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = lead.sourceProperty.title,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            Column(
                horizontalAlignment = Alignment.End,
                verticalArrangement = Arrangement.spacedBy(5.dp)
            ) {
                Text(
                    text = lead.checkedInAt.leadTime(),
                    color = theme.primaryText,
                    style = MaterialTheme.typography.labelSmall,
                    maxLines = 1
                )
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    if (lead.hasSentMessageFollowUp) {
                        CompactFollowUpBadge()
                    }
                    if (lead.isInquiry) {
                        CompactInquiryBadge(isGeneral = lead.isBuyerInquiry)
                    }
                    CompactTemperatureBadge(lead)
                }
            }
        }
    }
}

@Composable
private fun LeadCardSkeleton() {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(58.dp)
            .padding(horizontal = 2.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        HavnSkeletonBlock(
            modifier = Modifier.size(38.dp),
            shape = RoundedCornerShape(19.dp)
        )
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(7.dp)
        ) {
            HavnLeadSkeletonBlock(Modifier.fillMaxWidth(0.42f).height(14.dp))
            HavnLeadSkeletonBlock(Modifier.fillMaxWidth(0.62f).height(12.dp))
        }
        Column(
            horizontalAlignment = Alignment.End,
            verticalArrangement = Arrangement.spacedBy(7.dp)
        ) {
            HavnLeadSkeletonBlock(Modifier.width(72.dp).height(12.dp))
            HavnLeadSkeletonBlock(Modifier.width(92.dp).height(22.dp))
        }
    }
}

@Composable
private fun HavnLeadSkeletonBlock(modifier: Modifier = Modifier) {
    HavnSkeletonBlock(modifier, shape = RoundedCornerShape(7.dp))
}

@Composable
private fun LeadAvatar(name: String, avatarDataUrl: String?) {
    val theme = LocalHavnThemeTokens.current
    val initials = name
        .split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "H" }
    Box(
        modifier = Modifier
            .size(38.dp)
            .clip(RoundedCornerShape(19.dp))
            .background(Brush.linearGradient(listOf(theme.secondaryAccent.copy(alpha = 0.72f), theme.surface)))
            .border(1.dp, theme.primaryAccent.copy(alpha = 0.45f), RoundedCornerShape(19.dp)),
        contentAlignment = Alignment.Center
    ) {
        val rendered = HavnAsyncImage(
            dataUrl = avatarDataUrl,
            contentDescription = name,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )
        if (!rendered) {
            Text(
                text = initials,
                color = theme.primaryText,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun LargeLeadAvatar(name: String, avatarDataUrl: String?) {
    val theme = LocalHavnThemeTokens.current
    val initials = name
        .split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "H" }
    Box(
        modifier = Modifier
            .size(76.dp)
            .clip(RoundedCornerShape(38.dp))
            .background(Brush.linearGradient(listOf(theme.secondaryAccent.copy(alpha = 0.75f), theme.surface)))
            .border(1.dp, theme.primaryAccent.copy(alpha = 0.52f), RoundedCornerShape(38.dp)),
        contentAlignment = Alignment.Center
    ) {
        val rendered = HavnDatabaseImage(
            dataUrl = avatarDataUrl,
            previewDataUrl = havnContactAvatarPreviewSource(avatarDataUrl, 144),
            contentDescription = name,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )
        if (!rendered) {
            Text(
                text = initials,
                color = theme.primaryText,
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun CompactTemperatureBadge(lead: Lead) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val label = lead.temperature.localizedLabel(strings)
    val color = lead.temperature.temperatureAccent()
    Surface(
        shape = RoundedCornerShape(6.dp),
        color = color.copy(alpha = 0.9f),
        border = BorderStroke(1.dp, color.copy(alpha = 0.95f))
    ) {
        Text(
            text = label,
            color = lead.temperature.temperatureContentColor(theme.primaryText),
            style = MaterialTheme.typography.labelSmall,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
            maxLines = 1
        )
    }
}

@Composable
private fun CompactInquiryBadge(isGeneral: Boolean) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val label = if (isGeneral) strings.generalInquiry.substringBefore(' ') else strings.commonProperty
    val accent = if (isGeneral) Color.Black else theme.secondaryText
    val stripeColor = accent.copy(alpha = 0.25f)
    Surface(
        shape = RoundedCornerShape(6.dp),
        color = if (isGeneral) Color.Black else Color.Transparent,
        border = BorderStroke(1.dp, accent.copy(alpha = if (isGeneral) 0.9f else 0.65f))
    ) {
        Box(
            modifier = Modifier
                .height(24.dp)
                .width(72.dp),
            contentAlignment = Alignment.Center
        ) {
            if (!isGeneral) {
                Canvas(modifier = Modifier.matchParentSize()) {
                    val spacing = 10f
                    var start = -size.height
                    while (start < size.width) {
                        drawLine(
                            color = stripeColor,
                            start = Offset(start, size.height),
                            end = Offset(start + size.height, 0f),
                            strokeWidth = 1.5f
                        )
                        start += spacing
                    }
                }
            }
            Text(
                text = label,
                color = if (isGeneral) Color.White else theme.primaryText,
                style = MaterialTheme.typography.labelSmall,
                fontWeight = if (isGeneral) FontWeight.Bold else FontWeight.Medium,
                maxLines = 1
            )
        }
    }
}

@Composable
private fun InquiryTypeHeader(isGeneral: Boolean) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val accent = if (isGeneral) Color.Black else theme.secondaryText
    val label = if (isGeneral) strings.generalInquiry else strings.propertyInquiry
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(10.dp),
        color = if (isGeneral) Color.Black else accent.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, accent.copy(alpha = if (isGeneral) 0.72f else 0.4f))
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 9.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            HavnIcon(
                icon = if (isGeneral) HavnIconKey.Search else HavnIconKey.Home,
                contentDescription = null,
                tint = if (isGeneral) Color.White else accent,
                modifier = Modifier.size(17.dp)
            )
            Text(
                text = label,
                color = if (isGeneral) Color.White else theme.primaryText,
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.SemiBold
            )
        }
    }
}

@Composable
private fun CompactFollowUpBadge() {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        shape = RoundedCornerShape(6.dp),
        color = theme.primaryAccent.copy(alpha = 0.9f),
        border = BorderStroke(1.dp, theme.secondaryAccent)
    ) {
        Text(
            text = strings.commonFollowUps.removeSuffix("s"),
            color = theme.background,
            style = MaterialTheme.typography.labelSmall,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
            maxLines = 1
        )
    }
}

@Composable
private fun LeadFilters(
    searchQuery: String,
    selectedPropertyId: String?,
    selectedTemperature: LeadTemperature?,
    properties: List<LeadPropertySource>,
    onSearchChanged: (String) -> Unit,
    onPropertySelected: (String?) -> Unit,
    onTemperatureSelected: (LeadTemperature?) -> Unit
) {
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            HavnTextField(
                value = searchQuery,
                onValueChange = onSearchChanged,
                label = strings.searchLeads,
                placeholder = strings.searchLeadsPlaceholder,
                leadingIcon = HavnIconKey.Search
            )

            LeadTemperatureSegmentedControl(
                selectedTemperature = selectedTemperature,
                includeAll = true,
                onTemperatureSelected = onTemperatureSelected
            )

            HavnButton(
                text = strings.allProperties,
                onClick = { onPropertySelected(null) },
                style = if (selectedPropertyId == null) HavnButtonStyle.Primary else HavnButtonStyle.Secondary,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Properties
            )
            properties.forEach { property ->
                HavnButton(
                    text = property.title,
                    onClick = { onPropertySelected(property.id) },
                    style = if (selectedPropertyId == property.id) HavnButtonStyle.Primary else HavnButtonStyle.Ghost,
                    modifier = Modifier.fillMaxWidth(),
                    leadingIcon = HavnIconKey.Home
                )
            }

        }
    }
}

@Composable
private fun LeadDetailHome(
    state: LeadsUiState,
    viewModel: LeadsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    onSendFollowUp: (String) -> Unit,
    onFollowUpCompleted: () -> Unit,
    onCreateTask: (Lead) -> Unit,
    canUseAiFollowUps: Boolean,
    onAiUpgradeRequired: () -> Unit,
    detailBackLabel: String?,
    onBackFromDetail: (() -> Unit)?,
    linkedTasks: List<HavnTask>,
    onOpenTask: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val followUpsState by followUpsViewModel.uiState.collectAsState()
    val lead = state.selectedLead
    if (lead == null) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Brush.verticalGradient(listOf(theme.gradientStart, theme.background, theme.gradientEnd)))
                .padding(20.dp),
            contentAlignment = Alignment.Center
        ) {
            EmptyState(
                title = strings.leadUnavailableTitle,
                body = strings.leadUnavailableBody,
                actionText = strings.backToLeads,
                onAction = viewModel::closeDetail
            )
        }
        return
    }

    LeadDetailContent(
        lead = lead,
        isDetailLoading = state.isDetailLoading,
        isSaving = state.isSaving,
        error = state.error,
        followUpsViewModel = followUpsViewModel,
        onFollowUpCompleted = onFollowUpCompleted,
        onCreateTask = onCreateTask,
        canUseAiFollowUps = canUseAiFollowUps,
        onAiUpgradeRequired = onAiUpgradeRequired,
        detailBackLabel = detailBackLabel ?: strings.navLeads,
        onBack = onBackFromDetail ?: viewModel::closeDetail,
        linkedTasks = linkedTasks,
        onOpenTask = onOpenTask,
        onRefresh = { viewModel.openLead(lead.id) },
        onTemperatureSelected = viewModel::setTemperature,
        onDismissError = viewModel::clearError
    )
}

@Composable
fun LeadDetailContent(
    lead: Lead,
    isDetailLoading: Boolean,
    isSaving: Boolean,
    error: String?,
    followUpsViewModel: FollowUpsViewModel,
    onFollowUpCompleted: () -> Unit,
    onCreateTask: (Lead) -> Unit,
    canUseAiFollowUps: Boolean,
    onAiUpgradeRequired: () -> Unit,
    detailBackLabel: String,
    onBack: () -> Unit,
    linkedTasks: List<HavnTask>,
    onOpenTask: (String) -> Unit,
    onRefresh: () -> Unit,
    onTemperatureSelected: (LeadTemperature) -> Unit,
    onDismissError: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val followUpsState by followUpsViewModel.uiState.collectAsState()

    LaunchedEffect(lead.id, lead.sourceProperty.photoDataUrls.size) {
        followUpsViewModel.openSendFollowUpForLead(lead)
    }

    LaunchedEffect(followUpsState.completedFollowUpEventId) {
        if (followUpsState.completedFollowUpEventId != null) {
            onFollowUpCompleted()
            followUpsViewModel.consumeCompletedFollowUpEvent()
        }
    }

    HavnScreenFrame(
        eyebrow = "",
        title = lead.fullName,
        subtitle = "",
        backLabel = detailBackLabel,
        onBack = onBack,
        headerTrailing = {
            HavnWebRefreshAction(
                onRefresh = onRefresh,
                isRefreshing = isDetailLoading
            )
        }
    ) {
        Box {
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                HavnCard {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        if (lead.isInquiry) InquiryTypeHeader(isGeneral = lead.isBuyerInquiry)
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(14.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            LargeLeadAvatar(lead.fullName, lead.avatarDataUrl)
                            Column(
                                modifier = Modifier.weight(1f),
                                verticalArrangement = Arrangement.spacedBy(5.dp)
                            ) {
                                Text(
                                    text = lead.fullName,
                                    color = theme.primaryText,
                                    style = MaterialTheme.typography.headlineSmall,
                                    fontWeight = FontWeight.SemiBold,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                                lead.phone?.takeIf { it.isNotBlank() }?.let {
                                    Text(it, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
                                }
                                lead.email?.takeIf { it.isNotBlank() }?.let {
                                    Text(it, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                                }
                            }
                        }
                        if (lead.isBuyerInquiry) {
                            DetailLine("Location", listOfNotNull(lead.desiredLocation, lead.locationRadiusKm?.let { "$it km radius" }).joinToString(" · "))
                            DetailLine("Budget", listOfNotNull(lead.budgetMin?.toString(), lead.budgetMax?.toString()).joinToString(" – ").ifBlank { "Not specified" })
                            DetailLine("Timeline", lead.purchaseTimeline ?: "Not specified")
                            DetailLine("Property type", lead.desiredPropertyType ?: "Not specified")
                        } else {
                            DetailLine(strings.commonProperty, lead.sourceProperty.title)
                            DetailLine(strings.commonAddress, lead.sourceProperty.address)
                        }
                        DetailLine(
                            if (lead.isInquiry) strings.inquiryType else strings.commonOpenHouse,
                            if (lead.isBuyerInquiry) strings.generalInquiry else if (lead.isInquiry) strings.propertyInquiry else lead.sourceOpenHouse.displayTitle
                        )
                        DetailLine(strings.leadSource, lead.displayLeadSource(strings))
                        DetailLine(strings.commonCheckedIn, lead.checkedInAt.compactDateTime(strings))
                        DetailLine(strings.leadFollowUp, lead.nextFollowUpLine(strings))
                    }
                }

                HavnCard {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    LeadContactActionButton(
                        label = strings.leadActionCall,
                        icon = HavnIconKey.Phone,
                        onClick = { lead.phone?.let(HavnExternalActions::openPhone) },
                        modifier = Modifier.weight(1f),
                        enabled = !lead.phone.isNullOrBlank()
                    )
                    LeadContactActionButton(
                        label = strings.leadActionSms,
                        icon = HavnIconKey.Message,
                        onClick = {
                            lead.phone?.let {
                                HavnExternalActions.openSms(
                                    to = it,
                                    body = "${strings.greetingHi} ${lead.firstName(strings)},"
                                )
                            }
                        },
                        modifier = Modifier.weight(1f),
                        enabled = !lead.phone.isNullOrBlank()
                    )
                    LeadContactActionButton(
                        label = strings.leadActionEmail,
                        icon = HavnIconKey.Mail,
                        onClick = {
                            lead.email?.let {
                                HavnExternalActions.openEmail(
                                    to = it,
                                    subject = "${strings.followUpDefaultSubject} ${lead.sourceProperty.title}",
                                    body = "${strings.greetingHi} ${lead.firstName(strings)},"
                                )
                            }
                        },
                        modifier = Modifier.weight(1f),
                        enabled = !lead.email.isNullOrBlank()
                    )
                }
            }

            HavnButton(
                text = strings.createTask,
                onClick = { onCreateTask(lead) },
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Check
            )
            LinkedTasksSection(
                tasks = linkedTasks.filter { it.leadId == lead.id },
                onOpenTask = onOpenTask
            )

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(lead.statusEditorTitle(strings), color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                    LeadTemperatureSegmentedControl(
                        selectedTemperature = lead.temperature,
                        includeAll = false,
                        enabled = !isSaving,
                        onTemperatureSelected = { temperature ->
                            if (temperature != null) onTemperatureSelected(temperature)
                        }
                    )
                }
            }

            FollowUpComposerContent(
                state = followUpsState,
                viewModel = followUpsViewModel,
                showLeadSummary = false,
                showAiAssistant = true,
                canUseAiAssistant = canUseAiFollowUps,
                onAiUpgradeRequired = onAiUpgradeRequired
            )

            error?.let { message ->
                UpgradeableErrorText(message = message, onDismiss = onDismissError, onUpgradePlan = onAiUpgradeRequired)
            }
            }
            HavnRefreshOverlayIndicator(
                isRefreshing = isDetailLoading,
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .padding(top = 2.dp)
            )
        }
    }
}

@Composable
private fun UpgradeableErrorText(message: String, onDismiss: () -> Unit, onUpgradePlan: () -> Unit) {
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Text(message, color = HavnDanger, style = MaterialTheme.typography.bodyLarge)
            if (message.isUpgradePrompt()) {
                HavnButton(
                    text = strings.upgradePlan,
                    onClick = onUpgradePlan,
                    modifier = Modifier.fillMaxWidth(),
                    style = HavnButtonStyle.Primary
                )
            }
            HavnButton(
                text = strings.commonDismiss,
                onClick = onDismiss,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Ghost
            )
        }
    }
}

private fun String.isUpgradePrompt(): Boolean {
    val normalized = lowercase()
    return "upgrade" in normalized || "current plan" in normalized || "limit reached" in normalized || "storage limit" in normalized || "ai " in normalized || "ai-" in normalized
}

private fun Lead.firstName(strings: com.havn.app.core.localization.HavnStrings): String =
    fullName.substringBefore(" ").ifBlank { strings.leadGenericGreetingName }

private fun Lead.statusEditorTitle(strings: com.havn.app.core.localization.HavnStrings): String =
    if (isInquiry) strings.inquiryStatus else strings.leadTemperature

@Composable
private fun LeadContactActionButton(
    label: String,
    icon: HavnIconKey,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val theme = LocalHavnThemeTokens.current
    val contentColor = if (enabled) theme.primaryAccent else theme.mutedText
    Surface(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier.height(64.dp),
        shape = RoundedCornerShape(16.dp),
        color = theme.surface.copy(alpha = if (enabled) 0.74f else 0.42f),
        border = BorderStroke(1.dp, if (enabled) theme.primaryAccent.copy(alpha = 0.45f) else theme.border.copy(alpha = 0.55f))
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 8.dp),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(icon, contentDescription = label, tint = contentColor, modifier = Modifier.size(19.dp))
            Spacer(Modifier.width(7.dp))
            Text(
                label,
                color = if (enabled) theme.primaryText else theme.mutedText,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Clip
            )
        }
    }
}

@Composable
private fun DetailTemperatureBadge(temperature: LeadTemperature) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val color = temperature.temperatureAccent()
    Surface(
        shape = RoundedCornerShape(7.dp),
        color = color.copy(alpha = 0.9f),
        border = BorderStroke(1.dp, color)
    ) {
        Text(
            text = "${temperature.localizedLabel(strings)} ${strings.leadLabelSuffix}",
            color = temperature.temperatureContentColor(theme.primaryText),
            style = MaterialTheme.typography.labelSmall,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 5.dp),
            maxLines = 1
        )
    }
}

@Composable
private fun LeadDetailPanel(content: @Composable ColumnScope.() -> Unit) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        color = theme.surface.copy(alpha = 0.58f),
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.78f))
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
            content = content
        )
    }
}

@Composable
private fun LeadSection(
    title: String,
    body: String
) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            title,
            color = theme.primaryAccent,
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.SemiBold
        )
        Text(
            body,
            color = theme.primaryText,
            style = MaterialTheme.typography.bodyMedium,
            maxLines = 4,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun LeadDivider() {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(1.dp)
            .background(theme.border.copy(alpha = 0.62f))
    )
}

@Composable
private fun LeadsDetailContent(
    state: LeadsUiState,
    viewModel: LeadsViewModel
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val lead = state.selectedLead
    if (lead == null) {
        EmptyState(
            title = strings.leadUnavailableTitle,
            body = strings.leadUnavailableBody,
            actionText = strings.backToLeads,
            onAction = viewModel::closeDetail
        )
        return
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            if (lead.isInquiry) InquiryTypeHeader(isGeneral = lead.isBuyerInquiry)
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
                Column(Modifier.weight(1f)) {
                    Text(lead.fullName, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
                    Text(lead.contactLine, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
                }
                HavnStatusBadge(lead.temperature.toStatus())
            }
            DetailLine(strings.commonProperty, lead.sourceProperty.title)
            DetailLine(strings.commonAddress, lead.sourceProperty.address)
            DetailLine(
                if (lead.isInquiry) strings.inquiryType else strings.commonOpenHouse,
                if (lead.isBuyerInquiry) strings.generalInquiry else if (lead.isInquiry) strings.propertyInquiry else lead.sourceOpenHouse.displayTitle
            )
            DetailLine(strings.leadSource, lead.displayLeadSource(strings))
            DetailLine(strings.commonCheckedIn, lead.checkedInAt.compactDateTime(strings))
            lead.intentScore?.let { DetailLine(strings.leadIntentScore, it.toString()) }
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(lead.statusEditorTitle(strings), color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            LeadTemperatureSegmentedControl(
                selectedTemperature = lead.temperature,
                includeAll = false,
                enabled = !state.isSaving,
                onTemperatureSelected = { temperature ->
                    if (temperature != null) viewModel.setTemperature(temperature)
                }
            )
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.privateAgentNotes, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            HavnTextField(
                value = state.notesDraft,
                onValueChange = viewModel::updateNotesDraft,
                label = strings.notes,
                placeholder = strings.notesPlaceholder,
                singleLine = false,
                enabled = !state.isSaving
            )
            HavnButton(
                text = strings.saveNotes,
                onClick = viewModel::saveNotes,
                loading = state.isSaving,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Check
            )
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.followUpHistory, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            if (lead.followUps.isEmpty()) {
                Text(
                    strings.noFollowUpsLogged,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyLarge
                )
            } else {
                lead.followUps.forEach { followUp -> FollowUpRow(followUp) }
            }
        }
    }
}

@Composable
private fun DetailLine(label: String, value: String) {
    val theme = LocalHavnThemeTokens.current
    Column {
        Text(label, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        Spacer(Modifier.height(3.dp))
        Text(
            value,
            color = theme.primaryText,
            style = MaterialTheme.typography.bodyLarge,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
fun LeadTemperatureSegmentedControl(
    selectedTemperature: LeadTemperature?,
    includeAll: Boolean,
    onTemperatureSelected: (LeadTemperature?) -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val segments = buildList {
        if (includeAll) add(LeadTemperatureSegment(strings.totalMetric, null))
        LeadTemperature.entries.forEach { temperature ->
            add(LeadTemperatureSegment(temperature.localizedLabel(strings), temperature))
        }
    }

    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        color = theme.surface,
        border = androidx.compose.foundation.BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(3.dp),
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            segments.forEach { segment ->
                val selected = selectedTemperature == segment.temperature
                if (segment.temperature == null) {
                    HavnListFilterChip(
                        label = segment.label,
                        selected = selected,
                        onClick = { onTemperatureSelected(segment.temperature) },
                        modifier = Modifier.weight(1f),
                        enabled = enabled
                    )
                } else {
                    LeadTemperatureSegmentChip(
                        label = segment.label,
                        temperature = segment.temperature,
                        selected = selected,
                        onClick = { onTemperatureSelected(segment.temperature) },
                        modifier = Modifier.weight(1f),
                        enabled = enabled
                    )
                }
            }
        }
    }
}

@Composable
private fun LeadTemperatureSegmentChip(
    label: String,
    temperature: LeadTemperature,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val theme = LocalHavnThemeTokens.current
    val accent = temperature.temperatureAccent()
    val background = if (selected) accent else accent.copy(alpha = 0.16f)
    val contentColor = if (selected) temperature.temperatureContentColor(theme.primaryText) else accent
    Surface(
        shape = RoundedCornerShape(9.dp),
        color = if (enabled) background else theme.card,
        border = BorderStroke(1.dp, if (selected) contentColor.copy(alpha = 0.55f) else accent.copy(alpha = 0.46f)),
        modifier = modifier
            .height(HavnListDefaults.filterChipHeight)
            .clip(RoundedCornerShape(9.dp))
            .then(if (enabled) Modifier.clickable(onClick = onClick) else Modifier)
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 4.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = label,
                color = if (enabled) contentColor else theme.mutedText,
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

private data class LeadTemperatureSegment(
    val label: String,
    val temperature: LeadTemperature?
)

@Composable
private fun FollowUpRow(followUp: LeadFollowUp) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Column {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            Text(
                followUp.channel.toSentenceCaseLabel(),
                color = theme.primaryAccent,
                style = MaterialTheme.typography.labelLarge
            )
            Text(
                followUp.status.toSentenceCaseLabel(),
                color = theme.secondaryText,
                style = MaterialTheme.typography.labelLarge
            )
            Spacer(Modifier.width(1.dp))
        }
        Text(
            followUp.subject?.takeIf { it.isNotBlank() } ?: strings.leadFollowUp,
            color = theme.primaryText,
            style = MaterialTheme.typography.titleMedium
        )
        followUp.body?.takeIf { it.isNotBlank() }?.let {
            Text(it, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
        }
        Text(
            (followUp.completedAt ?: followUp.dueAt ?: followUp.createdAt).orEmpty().compactDateTime(strings),
            color = theme.secondaryText,
            style = MaterialTheme.typography.labelLarge
        )
    }
}

private fun LeadTemperature.toStatus(): HavnStatus = when (this) {
    LeadTemperature.Hot -> HavnStatus.Hot
    LeadTemperature.Warm -> HavnStatus.Warm
    LeadTemperature.Cold -> HavnStatus.Cold
}

private fun LeadTemperature.temperatureAccent(): Color = when (this) {
    LeadTemperature.Hot -> Color(0xFFE85D4A)
    LeadTemperature.Warm -> Color(0xFFD99A32)
    LeadTemperature.Cold -> Color(0xFF35C9D0)
}

private fun LeadTemperature.temperatureContentColor(defaultTextColor: Color): Color =
    if (this == LeadTemperature.Cold) Color(0xFF031316) else defaultTextColor

private fun LeadTemperature.localizedLabel(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    LeadTemperature.Hot -> strings.commonHot
    LeadTemperature.Warm -> strings.commonWarm
    LeadTemperature.Cold -> strings.commonCold
}

private val Lead.preferredContactLine: String
    get() = phone?.takeIf { it.isNotBlank() }
        ?: email?.takeIf { it.isNotBlank() }
        ?: contactLine

private fun Lead.interestSummary(strings: com.havn.app.core.localization.HavnStrings): String =
    intentScore?.let { "${strings.leadIntentScore} $it ${strings.commonProperty.lowercase()} ${sourceProperty.title}" }
        ?: "${strings.leadInterestedIn} ${sourceProperty.title}"

private fun Lead.nextFollowUpLine(strings: com.havn.app.core.localization.HavnStrings): String =
    followUps
        .firstOrNull { it.isSentMessageFollowUp }
        ?.let { followUp ->
            val due = followUp.completedAt ?: followUp.dueAt ?: followUp.createdAt
            due?.leadReadableDateTime() ?: followUp.status.toSentenceCaseLabel()
        }
        ?: strings.leadNoFollowUpScheduled

private val Lead.hasSentMessageFollowUp: Boolean
    get() = followUps.any { it.isSentMessageFollowUp }

private val LeadFollowUp.isSentMessageFollowUp: Boolean
    get() = (status == "completed" || !completedAt.isNullOrBlank()) &&
        (channel == "email" || channel == "sms")

private fun String.toSentenceCaseLabel(): String =
    replace("_", " ")
        .lowercase()
        .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }

private fun Lead.displayLeadSource(strings: com.havn.app.core.localization.HavnStrings): String = when {
    !openHouseId.isNullOrBlank() -> strings.commonOpenHouse
    !source.isNullOrBlank() -> source.toSentenceCaseLabel()
    else -> strings.notSpecified
}

private fun String.leadTime(): String {
    if (isBlank()) return "-"
    return readableLeadDateTime(includeYear = false)
}

private fun String.leadReadableDateTime(): String {
    if (isBlank()) return "-"
    return readableLeadDateTime(includeYear = true)
}

private fun String.compactDateTimeFallback(): String = cleanIsoDateTime()

private fun String.compactDateTime(strings: com.havn.app.core.localization.HavnStrings): String {
    if (isBlank()) return strings.commonNotScheduled
    return readableLeadDateTime(includeYear = true)
}

private fun String.readableLeadDateTime(includeYear: Boolean): String {
    val clean = cleanIsoDateTime()
    val date = clean.substringBefore(" ")
    val time = clean.substringAfter(" ", "")
    val dateLabel = date.readableLeadDate(includeYear)
    val timeLabel = time.readableLeadTime()
    return when {
        dateLabel != null && timeLabel != null -> "$dateLabel, $timeLabel"
        dateLabel != null -> dateLabel
        timeLabel != null -> timeLabel
        else -> clean
    }
}

private fun String.cleanIsoDateTime(): String =
    replace("T", " ")
        .substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")

private fun String.readableLeadDate(includeYear: Boolean): String? {
    val parts = split("-")
    if (parts.size != 3) return null
    val year = parts[0].takeIf { it.length == 4 } ?: return null
    val month = parts[1].toIntOrNull()?.let { leadMonthNames.getOrNull(it - 1) } ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return if (includeYear) "$month $day, $year" else "$month $day"
}

private fun String.readableLeadTime(): String? {
    if (":" !in this) return null
    val hour = substringBefore(":").toIntOrNull() ?: return null
    val minute = substringAfter(":").take(2).padStart(2, '0')
    val displayHour = when {
        hour == 0 -> 12
        hour > 12 -> hour - 12
        else -> hour
    }
    val suffix = if (hour >= 12) "PM" else "AM"
    return "$displayHour:$minute $suffix"
}

private val leadMonthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

private fun LeadsMode.navigationDepth(): Int = when (this) {
    LeadsMode.List -> 0
    LeadsMode.Detail -> 1
}
